import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

// Components
import { ControlsButton } from '../../ControlsButton'
import { FormRow } from '../../Forms/FormRow'

// Utils
import { formatValueByType } from '../../../_helpers'
import { Button } from '../../Button'
import { ButtonRow } from '../../layout/ButtonRow'
import { S_BusinessNote } from './BusinessNote.styles'

export const BusinessNote = ({
  allowEdit = false,
  allowRemove = false,
  allowPin = false,
  overflowNoteSize = 300,
  note = {},
  onRemove = () => {},
  onUpdate = () => {},
  onPin = () => {},
}) => {
  const [hover, setHover] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isPinned, setIsPinned] = useState(!!note.pinned_on)
  const [localNote, setLocalNote] = useState(note)
  const [isSeeingMore, setIsSeeingMore] = useState(false)
  const allowActions = allowEdit || allowRemove || allowPin
  const showActions = allowActions && !isEditing && hover
  const hasNoteChanged = note && note.note !== localNote.note

  useEffect(() => {
    if (isEditing) return
    setLocalNote(note)
  }, [note.note])

  const handleCancel = () => {
    setIsEditing(false)
    setLocalNote(note)
  }

  const handleUpdate = () => {
    onUpdate(localNote)
    setIsEditing(false)
  }

  const handleNoteChange = (value) => {
    setLocalNote((prevNote) => ({ ...prevNote, note: value }))
  }

  const handleNotePinned = () => {
    onPin(!isPinned)
    setIsPinned(!isPinned)
  }

  return (
    <S_BusinessNote
      allowActions={allowActions}
      hover={hover}
      key={note.id}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onMouseOver={() => {
        if (hover) return
        setHover(true)
      }}
    >
      {note.note ? (
        <div className="wrapper">
          <div className="actions-bar">
            <p className="created-by">
              {`${note.created_by.first_name || ''} ${note.created_by.last_name || ''}`}
              <span className="created-at">{formatValueByType(note.created_at, 'dateTime')}</span>
              {isPinned && <span className="pinned-on">Pinned on {formatValueByType(note.pinned_on, 'date')}</span>}
            </p>

            <div className="actions">
              {showActions && (
                <>
                  {allowPin && (
                    <ControlsButton
                      handleClick={handleNotePinned}
                      icon={'pin'}
                      variant={isPinned ? 'active' : 'normal'}
                    />
                  )}
                  {allowEdit && (
                    <ControlsButton
                      handleClick={() => {
                        setIsEditing(true)
                      }}
                      icon={'edit'}
                    />
                  )}
                  {allowRemove && (
                    <ControlsButton
                      handleClick={() => {
                        onRemove(note)
                      }}
                      icon={'delete'}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          <div className="note-content">
            {isEditing ? (
              <>
                <FormRow defaultValue={note.note} label="" onChange={handleNoteChange} type="textarea" />
                <ButtonRow align="right">
                  <Button className="compact secondary" onClick={handleCancel} text="Cancel" />
                  <Button className="compact primary" disabled={!hasNoteChanged} onClick={handleUpdate} text="Save" />
                </ButtonRow>
              </>
            ) : (
              <>
                <span>{note.note.substring(0, overflowNoteSize - 1)}</span>
                {note.note.length > overflowNoteSize &&
                  (isSeeingMore ? (
                    <>
                      <span>{note.note.substring(overflowNoteSize + 1)}</span>
                      <span className="note-expand-button" onClick={() => setIsSeeingMore(false)}>
                        See Less
                      </span>
                    </>
                  ) : (
                    <>
                      ...
                      <span className="note-expand-button" onClick={() => setIsSeeingMore(true)}>
                        See More
                      </span>
                    </>
                  ))}
              </>
            )}
          </div>
        </div>
      ) : (
        'Empty note'
      )}
    </S_BusinessNote>
  )
}

BusinessNote.propTypes = {
  allowEdit: PropTypes.bool,
  allowRemove: PropTypes.bool,
  overflowNoteSize: PropTypes.number,
  allowPin: PropTypes.bool,
  note: PropTypes.object,
  onRemove: PropTypes.func,
  onUpdate: PropTypes.func,
  onPin: PropTypes.func,
}
